// Frameworks — domain entity model. Shared by frontend + backend.
//
// A Framework is a best-practice path a tenant follows to reach a maturity
// outcome in one of the 8 GC Coach domains. The library is authored centrally
// by content editors; tenants activate the frameworks they want and run them
// as project-managed instances. The hierarchy is:
//
//   Domain (D1..D8)
//     └── Framework (Title · Details · Tags · Maturity target · Deliverables[])
//           └── Phase (Title · Details · Tags · Reference documents[])
//                 └── Activity (Title · Details · Tags · Duration · Deps ·
//                                Stakeholders · Documents[reference | template])
//
// Phase + Activity are NESTED inside Framework (single editorial atom);
// activation creates a separate FrameworkInstance per tenant (modeled
// separately, lands with the tenant-portal SPA in Phase 1).
//
// Design references:
//   - design/UI : UX v1.0/for-engineering-frameworks/engineering-brief.md
//   - design/UI : UX v1.0/for-engineering-frameworks/design-frameworks/
//     (the designer's locked README + strings spec + interactive mock)
//   - design/UI : UX v1.0/for-designer-frameworks-v2-model-and-consumer/
//     (v2 handoff — the shape the fields below describe)
//   - memory/project_gc_coach_product_spine.md (product spine memory)

import type { Domain } from './domains.js';
// Reuse the existing 5-level ladder from personae-api rather than defining
// a parallel one. One MaturityLevel type across the codebase = one source
// of truth for level naming + ordering. See MR 1 commit message for the
// vocabulary-mismatch note vs the designer's spec.
import type { MaturityLevel } from './personae-api.js';
export type { MaturityLevel };
export { ALL_MATURITY_LEVELS } from './personae-api.js';

/** Opaque, stable IDs — Mongo ObjectId serialized as 24-char hex (Framework)
 * or short opaque tokens (Phase / Activity — child of Framework, no separate
 * collection in V1 since they're nested). */
export type FrameworkId = string;
export type PhaseId = string;
export type ActivityId = string;

// ─── Editorial lifecycle ────────────────────────────────────────────────────

/**
 * Editorial status — managed by content_editor + super_admin via the admin
 * portal. Distinct from instance status (which is per-tenant).
 *
 * - `draft`     — being authored; not visible to tenants
 * - `published` — visible in the tenant library; tenants can activate
 * - `retired`   — no new activations allowed; existing instances continue
 *                 to completion. No hard delete while instances exist
 *                 (mirrors KB posture).
 */
export type FrameworkEditorialStatus = 'draft' | 'published' | 'retired';

export const ALL_FRAMEWORK_EDITORIAL_STATUSES: ReadonlyArray<FrameworkEditorialStatus> = [
  'draft',
  'published',
  'retired',
] as const;

/** UI filter type — includes `'all'` for "show every status". Mirrors the
 *  pattern used in users-api + tenants-api. */
export type FrameworkEditorialStatusFilter = FrameworkEditorialStatus | 'all';

// ─── Maturity target (v2 — replaces QCD) ───────────────────────────────────
// `MaturityLevel` + `ALL_MATURITY_LEVELS` are re-exported from personae-api
// (see the import block above). They use the existing codebase vocabulary
// (initial / developing / established / advanced / optimized).

/**
 * A framework's maturity contract: completing this framework advances the
 * tenant's maturity in the target domain to the target level. The advance
 * is evidence-based (see designer decision Q6 + engineering answer Q3) —
 * the tenant confirms the shift on the completion screen; running the
 * framework doesn't auto-advance.
 *
 * Load-bearing for the consumer library's recommendation bands
 * (RECOMMENDED NEXT / ALREADY THERE / AMBITIOUS — see spec §Library).
 * Required for publish (readiness check).
 */
export interface MaturityTarget {
  domain: Domain;
  level: MaturityLevel;
}

// ─── Duration buckets (v2 — activity-level) ────────────────────────────────

/**
 * Duration bucket labels per the calibration from Olivier's Legal Strategic
 * Plan sanity-check. Editor picks the default at framework-authoring time;
 * tenant can override at activation if `tenant_override_allowed = true`.
 * UI renders the string label, never a raw number.
 */
export type DurationBucket =
  | 'lt_1_month'
  | '1_3_months'
  | '3_6_months'
  | '6_9_months'
  | '9_12_months'
  | '12_18_months'
  | '18_24_months';

export const ALL_DURATION_BUCKETS: ReadonlyArray<DurationBucket> = [
  'lt_1_month',
  '1_3_months',
  '3_6_months',
  '6_9_months',
  '9_12_months',
  '12_18_months',
  '18_24_months',
] as const;

export interface ActivityDuration {
  default_months: DurationBucket;
  /** Default true — tenant can shorten/lengthen at activation. */
  tenant_override_allowed: boolean;
}

// ─── Activity dependencies (v2 — cross-phase allowed) ──────────────────────

/**
 * Activity → activity dependency. Cross-phase is expected and drives the
 * consumer Timeline's real Gantt. There is no phase-level dependency
 * graph — a phase's effective ordering is derived from the activity
 * deps it contains.
 */
export interface ActivityDependency {
  activity_id: ActivityId;
}

// ─── Stakeholders (v2 — activity-level, predefined + freeform) ─────────────

/**
 * Starter stakeholder list. Editor combobox autocompletes on these; custom
 * values are allowed as freeform tags (e.g. "Head of AI Ethics"). Grouped
 * for the picker per the designer's spec (Internal legal / C-suite /
 * Business / Governance).
 *
 * Includes calibration additions "Top Management" (umbrella above specific
 * C-suite roles) + "Legal Leadership Team" (granularity within Internal
 * Legal) from Olivier's sanity-check.
 */
export const STAKEHOLDER_STARTER_LIST: ReadonlyArray<string> = [
  // Internal legal
  'Legal (internal)',
  'External Counsel',
  'Legal Leadership Team',
  // C-suite
  'CEO',
  'CFO',
  'COO',
  'CTO',
  'CHRO',
  'General Counsel',
  'Top Management',
  // Business functions
  'Procurement',
  'Sales',
  'Finance',
  'HR',
  'IT',
  'Compliance',
  'Marketing',
  'Operations',
  'R&D',
  // Governance
  'Board',
  'Audit Committee',
] as const;

// ─── Documents (v2 — attached to phase OR activity) ────────────────────────

/**
 * Where the document lives: a KB entry we already have (reusable across
 * frameworks) or an uploaded file scoped to this framework only.
 */
export type DocumentSource = 'kb_entry_ref' | 'uploaded_file';

/**
 * `reference` — checklist, guide, example. Not evaluated, not a deliverable.
 * `deliverable_template` — a template the tenant fills; producing one or
 *   more framework-level deliverables (via `produces_deliverable_ids`).
 *   Only valid at activity level (phase-level docs are always reference).
 */
export type DocumentRole = 'reference' | 'deliverable_template';

/**
 * Phase-level document. Role locked to `reference` — the deliverable
 * template concept is activity-scoped since deliverables are produced by
 * discrete work items.
 */
export interface PhaseDocument {
  source: DocumentSource;
  role: 'reference';
  /** Present iff source === 'kb_entry_ref'. */
  kb_entry_id?: string;
  /** Present iff source === 'uploaded_file'. Server-generated storage ref. */
  uploaded_file_ref?: string;
  /** Display title. Editor-authored for uploads; auto-populated from KB entry name for refs. */
  title: string;
}

/**
 * Activity-level document. Same source options as PhaseDocument plus the
 * role toggle. When role === 'deliverable_template', `produces_deliverable_ids`
 * must contain at least one framework-level deliverable id (enforced by the
 * publish-readiness rule — see MR 2 validator).
 */
export interface ActivityDocument {
  source: DocumentSource;
  role: DocumentRole;
  kb_entry_id?: string;
  uploaded_file_ref?: string;
  title: string;
  /**
   * Framework-level deliverable ids this template produces. Multi-valued
   * per the Q4 revision in the v2 doc (one template can feed several
   * deliverables — Olivier's Risk Map example). Only set when
   * role === 'deliverable_template'.
   */
  produces_deliverable_ids?: ReadonlyArray<string>;
}

// ─── Framework-level deliverables (v2 — the OUTPUTS) ───────────────────────

/**
 * Framework-level deliverable = a tangible output the tenant has at the
 * end of running this framework (AI Governance Policy, Risk Register,
 * Board Deck). Produced by one or more activity-level templates via
 * `ActivityDocument.produces_deliverable_ids`.
 *
 * `evaluation_rubric` supports the V1.x coach-evaluation loop — the
 * coach reads the rubric to judge whether a submitted template passes.
 * Optional in V1 (self-report path).
 */
export interface FrameworkDeliverable {
  id: string;
  name: string;
  description: string;
  evaluation_rubric?: string;
}

// ─── Activity (leaf — unit of work) ─────────────────────────────────────────

export interface Activity {
  id: ActivityId;
  title: string;
  description: string;
  tags: ReadonlyArray<string>;
  /** Authoring order within the parent phase. */
  order: number;
  /**
   * Bucketed duration + tenant-override flag. Optional at the type level
   * (server can persist a draft without it) but required for publish —
   * enforced by validateFrameworkPublishable.
   */
  duration?: ActivityDuration;
  /**
   * Cross-phase-allowed activity → activity dependencies. Feeds the
   * consumer Timeline Gantt + drift compute (planned_end derives from
   * start_date + this graph + durations).
   */
  dependencies?: ReadonlyArray<ActivityDependency>;
  /**
   * Stakeholders involved / impacted by this activity. Multi-valued;
   * suggests from STAKEHOLDER_STARTER_LIST but freeform entries allowed.
   */
  stakeholders?: ReadonlyArray<string>;
  /**
   * 0..N documents attached to this activity. Each can be `reference`
   * (checklist/guide/example) or `deliverable_template` (fills produce
   * a framework-level deliverable).
   */
  documents?: ReadonlyArray<ActivityDocument>;
}

// ─── Phase (intermediate — groups activities) ───────────────────────────────

export interface Phase {
  id: PhaseId;
  title: string;
  description: string;
  tags: ReadonlyArray<string>;
  /** Authoring order within the framework. */
  order: number;
  /** Nested activities. */
  activities: ReadonlyArray<Activity>;
  /**
   * 0..N reference documents scoped to the whole phase. Role is always
   * `reference` at phase level (deliverable templates live on activities,
   * since deliverables are produced by discrete work items — see
   * PhaseDocument).
   */
  documents?: ReadonlyArray<PhaseDocument>;
}

// ─── Framework (root — the editorial atom) ──────────────────────────────────

/**
 * Compact shape for the library list views. Used by:
 *   - View 8 (content-editor library management) in admin portal
 *   - View 1 (tenant library browse) in tenant portal
 *
 * Excludes long_description, maturity_target, deliverables, and the
 * nested phases array.
 * Full detail comes via GET /frameworks/:id.
 */
export interface FrameworkSummary {
  id: FrameworkId;
  domain: Domain;
  title: string;
  short_description: string;
  tags: ReadonlyArray<string>;
  status: FrameworkEditorialStatus;
  /**
   * Monotonically increasing per-publish version number. Citation references
   * resolve to the cited version (frozen), never to current — same posture
   * as KB. V1 ships the field but no rollback UI (see README §7).
   */
  version: number;
  phase_count: number;
  activity_count: number;
  /** Optional human-friendly estimated effort. Free-form. */
  estimated_effort_label: string | null;
  /** Number of tenants currently with an active or completed instance.
   *  Useful for the View 8 "ACTIVATED BY # TENANTS" column. */
  activation_count: number;
  /** ISO-8601. */
  created_at: string;
  /** ISO-8601; last server-side mutation. */
  updated_at: string;
  /** Display name of whoever last edited this framework. */
  last_edited_by: string;
}

/**
 * Full framework — includes long description, QCD objectives, and the
 * nested phases/activities tree. Returned by GET /frameworks/:id and by
 * the create / patch / publish / retire endpoints.
 */
export interface FrameworkDetail extends FrameworkSummary {
  long_description: string;
  phases: ReadonlyArray<Phase>;
  /** Display name of whoever created the framework (immutable). */
  created_by: string;
  /** ISO-8601, set when status flipped to 'published' the most recent time.
   *  Null while still draft / never published. Resets to a new timestamp on
   *  republish-after-retire. */
  published_at: string | null;
  /** ISO-8601, set when status flipped to 'retired'. Null otherwise. */
  retired_at: string | null;
  /**
   * Maturity contract: completing this framework advances the tenant's
   * maturity in this domain to this level (evidence-based advancement —
   * tenant confirms on completion). Required for publish; drives the
   * consumer library's recommendation bands. Optional at the type level
   * so drafts persist without one.
   */
  maturity_target?: MaturityTarget;
  /**
   * Framework-level OUTPUTS the tenant produces by running this framework
   * (AI Governance Policy, Risk Register, Board Deck). Each is produced
   * by one or more activity-level `deliverable_template` documents via
   * `ActivityDocument.produces_deliverable_ids`. Required for publish
   * (≥1 deliverable, and every declared deliverable claimed by a template).
   */
  deliverables?: ReadonlyArray<FrameworkDeliverable>;
}

// ─── Status counts (used by list responses) ─────────────────────────────────

export interface FrameworkStatusCounts {
  total: number;
  draft: number;
  published: number;
  retired: number;
}
